import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Sparkles, CreditCard, Minus, Star, Table2, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [appSettings, setAppSettings] = useState({ app_name: 'Thrive' });
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [currentPlan, setCurrentPlan] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const fetches = [
          fetch(`${API}/api/subscription/plans`),
          fetch(`${API}/api/app-settings/public`)
        ];
        if (token) {
          fetches.push(fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }));
        }
        const [plansRes, settingsRes, meRes] = await Promise.all(fetches);

        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlans(plansData);
        }
        
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setAppSettings(settings);
        }

        if (meRes?.ok) {
          const me = await meRes.json();
          setCurrentPlan(me.subscription_plan || 'free');
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const formatPrice = (plan) => {
    const price = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
    if (price === 0) return 'Free';
    
    if (billingCycle === 'annual') {
      const monthly = (price / 12).toFixed(2);
      return `$${monthly}/mo`;
    }
    return `$${price}/mo`;
  };

  // Feature categories for comparison table
  const featureCategories = [
    {
      id: 'hive_management',
      name: 'Hive Management',
      icon: '🏠',
      features: [
        { id: 'hives_basic', name: 'Add/Edit/Delete Hives' },
        { id: 'max_hives', name: 'Maximum Hives', type: 'dynamic', field: 'max_hives' },
        { id: 'qr_generation', name: 'QR Code Generation' },
        { id: 'qr_scanning', name: 'QR Code Scanning' },
        { id: 'hive_templates', name: 'Hive Templates' },
        { id: 'move_hive', name: 'Move Hives Between Apiaries' },
      ]
    },
    {
      id: 'team',
      name: 'Team & Crew',
      icon: '👥',
      features: [
        { id: 'max_crew', name: 'Team Members', type: 'dynamic', field: 'max_crew', fallbackField: 'max_members' },
        { id: 'crew_tasks', name: 'Assign Tasks to Crew' },
        { id: 'crew_inspections', name: 'Crew Can Record Inspections' },
      ]
    },
    {
      id: 'inspections',
      name: 'Inspections',
      icon: '📝',
      features: [
        { id: 'inspections_full', name: 'Full Inspection Form' },
        { id: 'max_inspections', name: 'Inspections per Month', type: 'dynamic', field: 'max_inspections', suffix: '/mo' },
        { id: 'photo_uploads', name: 'Photo Uploads' },
        { id: 'inspection_history_unlimited', name: 'Unlimited History' },
        { id: 'export_pdf', name: 'Export to PDF' },
      ]
    },
    {
      id: 'splits',
      name: 'Splits',
      icon: '✂️',
      features: [
        { id: 'split_wizard', name: 'Split Wizard' },
        { id: 'split_tracking', name: 'Track Split History' },
        { id: 'split_from_parent', name: 'Link to Parent Hive' },
      ]
    },
    {
      id: 'queen_management',
      name: 'Queen Management',
      icon: '👑',
      features: [
        { id: 'queen_tracking', name: 'Track Queen Details' },
        { id: 'queen_age', name: 'Queen Age Tracking' },
        { id: 'queen_rearing', name: 'Queen Rearing Calendar' },
        { id: 'requeening_alerts', name: 'Requeening Reminders' },
      ]
    },
    {
      id: 'swarm_management',
      name: 'Swarm Management',
      icon: '🐝',
      features: [
        { id: 'swarm_alerts', name: 'Swarm Risk Alerts' },
        { id: 'swarm_prevention', name: 'Swarm Prevention Checklist' },
        { id: 'swarm_capture', name: 'Log Swarm Captures' },
      ]
    },
    {
      id: 'ai_features',
      name: 'AI Features',
      icon: '🤖',
      features: [
        { id: 'ai_assistant', name: 'AI Beekeeping Assistant' },
        { id: 'voice_transcription', name: 'Voice-to-Text Notes' },
        { id: 'frame_scanner', name: 'AI Frame Scanner' },
        { id: 'ai_progress_reports', name: 'AI Progress Reports' },
      ]
    },
    {
      id: 'mite_control',
      name: 'Mite Control',
      icon: '🐛',
      features: [
        { id: 'mite_counts', name: 'Record Mite Counts' },
        { id: 'treatment_wizard', name: 'Treatment Plan Wizard' },
        { id: 'treatment_tracking', name: 'Active Treatment Tracking' },
      ]
    },
    {
      id: 'harvest',
      name: 'Harvest Tracking',
      icon: '🍯',
      features: [
        { id: 'harvest_logging', name: 'Log Harvests' },
        { id: 'harvest_charts', name: 'Harvest Charts' },
        { id: 'export_harvest', name: 'Export Harvest Data' },
      ]
    },
  ];

  // Check if a plan has a specific feature enabled
  const planHasFeature = (plan, featureId) => {
    if (plan.all_features) return true;
    return plan.enabled_features?.includes(featureId) || false;
  };

  // Get dynamic value from plan
  const getDynamicValue = (plan, feature) => {
    // Use nullish check so 0 is respected as a valid value
    let value = plan[feature.field];
    if (value === null || value === undefined) {
      value = feature.fallbackField ? plan[feature.fallbackField] : null;
    }
    if (value === null || value === undefined) {
      return feature.default || '—';
    }
    if (value >= 9999 || value === -1) {
      return 'Unlimited';
    }
    // Show "Owner only" for plans with 0 crew
    if (feature.field === 'max_crew' && value === 0) {
      return 'Owner only';
    }
    return `${value}${feature.suffix || ''}`;
  };

  // Sort plans by sort_order
  const sortedPlans = [...plans].sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

  // Feature Comparison Table Component
  const FeatureComparisonTable = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-stone-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="bg-gradient-to-r from-amber-100 to-orange-100">
              <th className="text-left p-4 font-semibold text-stone-800 min-w-[200px] sticky left-0 bg-gradient-to-r from-amber-100 to-amber-50 z-10">
                Feature
              </th>
              {sortedPlans.map(plan => (
                <th key={plan.plan_id || plan.id} className={`text-center p-4 min-w-[140px] ${(plan.plan_id || plan.id) === currentPlan ? 'bg-amber-50 ring-2 ring-amber-400 ring-inset' : ''}`}>
                  <div className="flex flex-col items-center gap-1">
                    {(plan.plan_id || plan.id) === currentPlan && (
                      <span className="bg-amber-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        YOUR PLAN
                      </span>
                    )}
                    {plan.is_popular && (plan.plan_id || plan.id) !== currentPlan && (
                      <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" /> POPULAR
                      </span>
                    )}
                    {plan.icon && (
                      <img
                        src={plan.icon.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL || ''}${plan.icon}` : plan.icon}
                        alt=""
                        className="w-8 h-8 object-contain rounded-lg"
                      />
                    )}
                    <span className="font-bold text-stone-800">{plan.name}</span>
                    <span className="text-sm font-normal text-stone-500">
                      {formatPrice(plan)}
                    </span>
                    <Link to="/trial-signup">
                      <button className={`mt-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        plan.is_popular 
                          ? 'bg-amber-500 text-white hover:bg-amber-600' 
                          : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                      }`}>
                        {plan.button_text || 'Get Started'}
                      </button>
                    </Link>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureCategories.map((category) => (
              <React.Fragment key={category.id}>
                {/* Category Header Row */}
                <tr className="bg-stone-100">
                  <td 
                    colSpan={sortedPlans.length + 1} 
                    className="p-3 font-semibold text-stone-700 sticky left-0 bg-stone-100 z-10"
                  >
                    <span className="mr-2">{category.icon}</span>
                    {category.name}
                  </td>
                </tr>
                {/* Feature Rows */}
                {category.features.map((feature, featureIndex) => (
                  <tr 
                    key={feature.id} 
                    className={`border-b border-stone-100 hover:bg-amber-50/50 transition-colors ${
                      featureIndex % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                    }`}
                  >
                    <td className="p-3 text-sm text-stone-700 sticky left-0 bg-inherit z-10 border-r border-stone-100">
                      {feature.name}
                    </td>
                    {sortedPlans.map(plan => (
                      <td key={`${plan.plan_id || plan.id}-${feature.id}`} className="p-3 text-center">
                        {feature.type === 'dynamic' ? (
                          <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold ${
                            getDynamicValue(plan, feature) === 'Unlimited' 
                              ? 'bg-green-100 text-green-700'
                              : getDynamicValue(plan, feature) === '—'
                                ? 'bg-stone-100 text-stone-400'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {getDynamicValue(plan, feature)}
                          </span>
                        ) : planHasFeature(plan, feature.id) ? (
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                            <Check className="w-4 h-4 text-green-600" strokeWidth={3} />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-stone-100">
                            <Minus className="w-4 h-4 text-stone-400" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-t border-stone-200">
        <div className="flex items-center justify-center">
          <p className="text-sm text-stone-600">
            All plans include a <strong>free trial</strong> • No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          <div className="flex items-center gap-2">
            <img src="/thrive-logo.png" alt={appSettings.app_name} className="w-8 h-8 object-contain" />
            <span className="font-bold text-stone-800">{appSettings.app_name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="text-center py-12 px-4">
        <h1 className="text-4xl font-bold text-stone-800 mb-4">
          Simple, transparent pricing
        </h1>
        <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-8">
          Start free and upgrade as your beekeeping operation grows. No hidden fees, cancel anytime.
        </p>

        {/* Current Plan Banner */}
        {currentPlan && (
          <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-5 py-2 mb-6 text-sm font-semibold" data-testid="current-plan-banner">
            <Crown className="w-4 h-4" />
            You're on the <span className="font-bold uppercase">{currentPlan}</span> plan
          </div>
        )}
        
        {/* View Toggle & Billing Toggle */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          {/* View Toggle */}
          <div className="flex items-center bg-white rounded-full p-1 border border-stone-200 shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                viewMode === 'table' 
                  ? 'bg-stone-800 text-white shadow' 
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <Table2 className="w-4 h-4" /> Compare Features
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                viewMode === 'cards' 
                  ? 'bg-stone-800 text-white shadow' 
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <LayoutGrid className="w-4 h-4" /> Plan Cards
            </button>
          </div>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-white rounded-full p-1 border border-stone-200 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-amber-500 text-white shadow' 
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'annual' 
                  ? 'bg-amber-500 text-white shadow' 
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              Annual
              <span className="ml-1 text-xs opacity-80">Save 17%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Plans Content */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-12 text-stone-500">Loading plans...</div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12 text-stone-500">No plans available</div>
        ) : viewMode === 'table' ? (
          <FeatureComparisonTable />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const price = formatPrice(plan);
              const isPopular = plan.is_popular;
              const isCurrent = (plan.plan_id || plan.id) === currentPlan;
              
              return (
                <Card 
                  key={plan.id} 
                  className={`relative overflow-hidden transition-all hover:shadow-xl ${
                    isCurrent ? 'border-2 border-amber-500 shadow-lg ring-2 ring-amber-200' : isPopular ? 'border-2 border-amber-400 shadow-lg scale-105' : 'border-stone-200'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute top-0 left-0 right-0 bg-amber-600 text-white text-center py-1.5 text-xs font-bold tracking-wide">
                      YOUR CURRENT PLAN
                    </div>
                  )}
                  {isPopular && !isCurrent && (
                    <div className="absolute top-0 right-0 bg-amber-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  
                  <CardHeader className={`text-center pb-2 ${isCurrent ? 'pt-10' : ''}`}>
                    <div className="mb-2">
                      {plan.icon ? (
                        <img
                          src={plan.icon.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL || ''}${plan.icon}` : plan.icon}
                          alt=""
                          className="w-12 h-12 object-contain mx-auto rounded-lg"
                        />
                      ) : (
                        <Crown className={`w-8 h-8 mx-auto ${isPopular ? 'text-amber-500' : 'text-stone-400'}`} />
                      )}
                    </div>
                    <CardTitle className="text-xl">{plan.name}</CardTitle>
                    <CardDescription className="text-stone-500">
                      {plan.description || plan.hive_range}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="text-center">
                    {/* Price */}
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-stone-800">{price}</span>
                      {billingCycle === 'annual' && plan.price_annual > 0 && (
                        <div className="text-sm text-stone-500 mt-1">
                          ${plan.price_annual}/year billed annually
                        </div>
                      )}
                    </div>
                    
                    {/* Key Stats */}
                    <div className="flex justify-center gap-4 mb-6 text-sm">
                      <div className="text-center">
                        <div className="font-bold text-stone-800">{plan.max_hives}</div>
                        <div className="text-stone-500">Hives</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-stone-800">{(plan.max_crew ?? plan.max_members ?? 0) > 0 ? (plan.max_crew ?? plan.max_members) : 'Owner'}</div>
                        <div className="text-stone-500">{(plan.max_crew ?? plan.max_members ?? 0) > 0 ? 'Crew' : 'Only'}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-stone-800">{plan.max_inspections || '∞'}</div>
                        <div className="text-stone-500">Inspections</div>
                      </div>
                    </div>
                    
                    {/* Features */}
                    {plan.features && plan.features.length > 0 && (
                      <ul className="text-left space-y-2 mb-6">
                        {plan.features.slice(0, 5).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {/* CTA Button */}
                    <Link to="/trial-signup">
                      <Button 
                        className={`w-full ${
                          isPopular 
                            ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                            : 'bg-stone-800 hover:bg-stone-900 text-white'
                        }`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get Started Free
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        
        {/* Free Account Banner */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-amber-100 to-orange-100 border-amber-200 max-w-2xl mx-auto">
            <CardContent className="py-8">
              <h3 className="text-xl font-bold text-stone-800 mb-2">
                Start with a Free Account
              </h3>
              <p className="text-stone-600 mb-4">
                Everyone starts with a free account. Upgrade when you&apos;re ready - no pressure!
              </p>
              <Link to="/trial-signup">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                  Create Free Account
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
